/**
 * useRealtimeVoice — WebRTC + GPT-Realtime client hook (P0-4 Phase 2)
 *
 * State machine: idle → connecting → connected → error (→ idle via disconnect).
 *
 * Conditions addressed (cycle 2 from 门下省 + Plan v2):
 *   C2: `window.__realtimeDataChannel` test backdoor — gated by
 *       `process.env.NODE_ENV !== 'production'` (verified at every assignment
 *       AND every delete site).
 *   C3: `renewSession()` 5-step graceful sequential (new fetch → new PC →
 *       new SDP exchange → new connected → close old). Rollback on any
 *       failure keeps old connection intact.
 *   C7: ephemeral_token + realtime_session_id stored in `useRef` ONLY.
 *       Never localStorage / sessionStorage / window-public.
 *   C8: getUserMedia with strict audio constraints + no video. NotAllowedError
 *       surfaced as JP UX message (`mic_denied`).
 *   C9: Race guard — concurrent `connect()` calls during connecting/connected
 *       return early. StrictMode cleanup via `isMountedRef`.
 *   C10: Renewal prompt 30s before `expires_at` (TOKEN_RENEWAL_PROMPT_LEAD_MS).
 *   M1: Cleanup defensive null check (`if (streamRef.current)`).
 *   M2: `onApplyOverlay` invoked via executor (array signature handled there).
 *
 * Captions: AI output transcript via canonical
 * `response.output_audio_transcript.done` (matches `.delta` namespace per OpenAI
 * Realtime API GA reference) + `response.output_audio_transcript.delta`;
 * retains legacy `response.audio_transcript.done` for backward compat
 * (post-merge hotfix P2 — see CHANGELOG `[v2.14.4-p0-4-phase2-post-merge-hotfix]`).
 *
 * Student utterance captions: P1-1 sidecar wires the data-channel events
 * `conversation.item.input_audio_transcription.completed` + `.delta` into an
 * internal `studentTranscripts` per-`item_id` map state (薄受け、Q4 太子判定:
 * 0 legacy fallback — canonical GA event names only). P2 turn-safety
 * (addresses 六次審査 finding): keyed by `item_id` so multiple in-flight
 * utterances + out-of-order `.completed` + `.delta` after `.completed` do
 * not corrupt prior utterance state. Shape:
 *   `{ [item_id]: { transcript: string, completed: boolean } }`
 * UI binding (caption display in
 * [RealtimeVoiceTutorPanel.js](../components/RealtimeVoiceTutorPanel.js) via
 * `Object.entries(studentTranscripts)`) shipped in Phase 3 Option A
 * (v2.14.4-p1-1-whisper-sidecar-p3-ui-binding). log_voice_turn schema extension
 * (Option B) shipped in v2.14.4-p1-1-whisper-sidecar-p3-option-b — see
 * "Outbound augmentation" paragraph below. RealtimeVoiceTrace.transcript_excerpt
 * persistence (Option C) shipped in v2.14.4-p1-1-whisper-sidecar-p3-option-c
 * — dual-column architecture: legacy `transcript_excerpt` TEXT retained for
 * backward compat (hotfix¹-⁴ invariants preserved); NEW `transcripts_json`
 * JSON column stores structured per-call entries with idempotent upsert,
 * line-aware cap, sanitization parity, and bootstrap migrator for pre-
 * Option-C rows.
 *
 * Outbound augmentation (Phase 3 Option B,
 * v2.14.4-p1-1-whisper-sidecar-p3-option-b): when the AI emits
 * `response.function_call_arguments.done` for `log_voice_turn`, the hook
 * augments the args with `student_transcripts: [{item_id, transcript, completed}]`
 * derived from the current per-`item_id` map state via `studentTranscriptsRef`
 * (ref ensures fresh value across .completed → tool-call event race windows).
 * Field is NOT declared in the OpenAI Realtime tool schema; backend dispatcher
 * accepts it via Dict[str, Any] router-layer propagation. AI does not populate
 * this field — frontend-only state plumbed through both
 * `RealtimeVoiceTrace.transcript_excerpt` (legacy line-format) AND
 * `transcripts_json` (Option C structured JSON list) via dual-write at the
 * dispatcher (atomic — if JSON build fails, legacy write also rolls back).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import {
  REALTIME_ERROR_CODE_MAP,
  REALTIME_VOICE_OPTIONS,
  TOKEN_RENEWAL_PROMPT_LEAD_MS,
  IDLE_ACTIVITY_EVENT_TYPES,
  MAX_SESSION_DURATION_MS,
  buildOpenAIRealtimeSdpUrl,
} from '../utils/realtimeVoiceConstants';
import { createUiActionExecutor } from '../utils/realtimeUiActionExecutor';
import { createSessionLimitController, computeRemainingMax } from '../utils/realtimeSessionLimit';
import { logClientWarn } from '../utils/safeLog';

// Default voice when caller does not set one (matches backend default).
const DEFAULT_VOICE = 'alloy';
// localStorage key for persisting the voice SELECTION — a non-secret UI
// preference, distinct from the ephemeral token (which stays useRef-only per the
// C7 contract). Frontend-only persistence; no backend settings / DB / cross-device.
const VOICE_STORAGE_KEY = 'graph_tutor_realtime_voice';
// Valid voice values, mirrored from the single source of truth
// (REALTIME_VOICE_OPTIONS) so a stored value that is missing or no longer
// supported falls back to DEFAULT_VOICE rather than being sent to the backend.
const VALID_VOICES = new Set(REALTIME_VOICE_OPTIONS.map((o) => o.value));

// Read the persisted voice, validating against the allowed set; fall back to
// DEFAULT_VOICE on missing / invalid / unavailable-storage.
function readPersistedVoice() {
  try {
    const stored = window.localStorage.getItem(VOICE_STORAGE_KEY);
    if (stored && VALID_VOICES.has(stored)) return stored;
  } catch (e) {
    // localStorage unavailable (SSR / privacy mode) — fall through to default.
  }
  return DEFAULT_VOICE;
}

// Default session lifetime requested from server (backend caps 60-3600s).
const DEFAULT_EXPIRES_AFTER_SECONDS = 600;
// Safety timeout while waiting for new RTCPeerConnection to reach `connected`
// during renewSession (10s — covers SDP round-trip + ICE).
const RENEW_CONNECT_TIMEOUT_MS = 10_000;
// getUserMedia audio constraints (no video — Realtime is audio-only).
const AUDIO_CONSTRAINTS = Object.freeze({
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
  },
  video: false,
});

/**
 * @param {Object} opts
 * @param {number} opts.sessionId
 *   Required — current learning session DB id (`Session.id` integer PK).
 *   Used as the EXPLICIT session-pinning anchor when POSTing /api/realtime/session.
 *   Replaces the prior `graphId` field as the ownership/pinning input
 *   (e2e-completion post-merge hotfix P1-1: backend now validates
 *   `DBSession.id == session_id AND user_id == current_user.id` before
 *   pinning, instead of silently selecting the user's most-recently-updated
 *   session which could be a different learning session than the panel was
 *   opened for).
 * @param {Object} opts.studentProfile
 *   Required — current student profile (used by panel for mount guard).
 * @param {(actions: Array<{type: string}>) => void} opts.onApplyOverlay
 *   Dashboard.js handleDashboardUiActions (array signature, M2).
 * @returns {Object} hook API surface (see destructure below).
 */
export default function useRealtimeVoice({ sessionId, studentProfile, onApplyOverlay }) {
  // ────────── State ──────────
  const [connectionState, setConnectionState] = useState('idle');
  const [errorCode, setErrorCode] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState(readPersistedVoice);
  const [aiCaptionText, setAiCaptionText] = useState('');
  // P1-1 + P2 turn-safety: Student utterance transcripts from Whisper sidecar,
  // keyed by OpenAI Realtime API `item_id` (NOT a single global string).
  // Shape: { [item_id]: { transcript: string, completed: boolean } }.
  // Multiple in-flight utterances + out-of-order `.completed` + `.delta`
  // after `.completed` are handled per-item_id without cross-utterance
  // corruption. UI binding (Option A) shipped in v2.14.4-p1-1-whisper-sidecar-p3-ui-binding —
  // consumed by RealtimeVoiceTutorPanel via Object.entries(studentTranscripts).
  // log_voice_turn schema extension (Option B) shipped in
  // v2.14.4-p1-1-whisper-sidecar-p3-option-b — backend dispatcher accepts the
  // map (idempotent upsert by item_id post-hotfix²) via Dict[str, Any]
  // router propagation. RealtimeVoiceTrace.transcript_excerpt persistence
  // (Option C) shipped in v2.14.4-p1-1-whisper-sidecar-p3-option-c —
  // dual-column architecture (legacy TEXT retained + NEW transcripts_json
  // JSON column with idempotent upsert + line-aware cap + bootstrap
  // migrator); informed by Option B's mixed-format payload shape.
  // Q3 cycle 2 承認: useState (NOT useRef) — Phase 3 で React component
  // re-render trigger として読む前提、aiCaptionText 既存 pattern と sibling
  // (aiCaptionText remains single string since AI side is one stream at a
  // time; student side can have overlapping items per OpenAI Realtime API).
  const [studentTranscripts, setStudentTranscripts] = useState({});
  const [modelUsed, setModelUsed] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [showRenewPrompt, setShowRenewPrompt] = useState(false);
  // D4: client-enforced Realtime voice session caps. `sessionEndingSoon` drives a
  // heads-up warning ~SESSION_LIMIT_WARNING_LEAD_MS before a limit; `sessionLimitNotice`
  // is shown AFTER an auto-disconnect to explain why. Both: null | 'idle' | 'max_duration'.
  const [sessionEndingSoon, setSessionEndingSoon] = useState(null);
  const [sessionLimitNotice, setSessionLimitNotice] = useState(null);

  // ────────── Refs (NEVER expose to window / storage) ──────────
  const pcRef = useRef(null);              // RTCPeerConnection
  const dcRef = useRef(null);              // RTCDataChannel
  const streamRef = useRef(null);          // MediaStream (mic)
  const ephemeralTokenRef = useRef(null);  // C7: useRef only
  const realtimeSessionIdRef = useRef(null);
  const remoteAudioRef = useRef(null);     // M2: <audio ref={...} autoPlay>
  const renewTimerRef = useRef(null);
  const isMountedRef = useRef(true);
  const studentProfileRef = useRef(studentProfile);
  const sessionIdRef = useRef(sessionId);
  const { i18n } = useTranslation();
  const selectedVoiceRef = useRef(selectedVoice);
  // i18n: pin the voice tutor's response language per realtime session.
  const languageRef = useRef(i18n.language);
  // Phase 3 Option B (v2.14.4-p1-1-whisper-sidecar-p3-option-b):
  // Mirror of `studentTranscripts` useState in a ref, kept synchronously
  // ahead of state via ref-FIRST-setState-SECOND assignment in the .completed
  // / .delta handlers below (C1 race fix v2 post-I3 GREEN exposure: the
  // setState-callback-inline pattern initially specified in the 中书省 plan
  // does NOT fire synchronously — React 18 batches setState and runs the
  // updater in commit phase, AFTER the current event handler returns; a
  // synchronous subsequent event in the same tick would still see the stale
  // ref). The ref is the sole accumulation source: copying committed state
  // back into it from an effect can regress the ref when a slow/concurrent
  // render commits between consecutive data-channel events.
  // C1 race fix: the `function_call_arguments.done` handler for
  // `log_voice_turn` reads `studentTranscriptsRef.current` to augment AI-emitted
  // tool args BEFORE executor send. The ref is updated SYNCHRONOUSLY before
  // setState is called, so subsequent reads in the same event tick observe
  // the just-completed item.
  const studentTranscriptsRef = useRef({});

  // P2-3 race-guard (post-e2e-completion sub-task): synchronous in-flight
  // flags set BEFORE any await. Replaces the state-based guard at L258
  // which was vulnerable to async setState (2 concurrent calls in the same
  // render closure both observed connectionState='idle').
  const connectInFlightRef = useRef(false);
  const renewInFlightRef = useRef(false);

  // D4: monotonic wall-clock deadline state. `sessionStartedAtRef` is the absolute
  // start of the current session (null until first 'connected'); an automatic
  // reconnect (401 auth-retry) preserves it so the budget is not reset. A
  // user-initiated connect clears it (fresh budget). `sessionLimitEndedRef` lets an
  // auto-end cancel an in-flight renewSession before it can revive the call.
  const sessionStartedAtRef = useRef(null);
  const sessionLimitEndedRef = useRef(false);
  const authReconnectRef = useRef(false);

  // D4: one session-limit controller per hook lifetime. Callbacks use stable
  // setState + refs (disconnectRef is resolved at call time, after its own decl).
  const sessionLimitRef = useRef(null);
  if (sessionLimitRef.current === null) {
    sessionLimitRef.current = createSessionLimitController({
      onWarn: (reason) => {
        if (!isMountedRef.current) return;
        // 'max_duration' is the hard cap → sticky; a later idle warn must not downgrade it.
        setSessionEndingSoon((prev) => (prev === 'max_duration' ? prev : reason));
      },
      onClearIdleWarn: () => {
        if (!isMountedRef.current) return;
        setSessionEndingSoon((prev) => (prev === 'idle' ? null : prev));
      },
      onEnd: (reason) => {
        if (!isMountedRef.current) return;
        // Mark ended BEFORE disconnecting so an in-flight renewSession aborts its swap
        // instead of reviving the call past the hard cap.
        sessionLimitEndedRef.current = true;
        setSessionLimitNotice(reason);
        if (disconnectRef.current) disconnectRef.current();
      },
    });
  }

  // Keep refs in sync (avoid stale closures in callbacks).
  useEffect(() => { studentProfileRef.current = studentProfile; }, [studentProfile]);
  useEffect(() => { sessionIdRef.current = sessionId; }, [sessionId]);
  useEffect(() => { selectedVoiceRef.current = selectedVoice; }, [selectedVoice]);
  useEffect(() => { languageRef.current = i18n.language; }, [i18n.language]);
  // Persist the voice selection across reloads (same browser/device). Writes the
  // default on first mount when nothing is stored, and self-heals an invalid
  // stored value (readPersistedVoice already coerced it to DEFAULT_VOICE).
  useEffect(() => {
    try { window.localStorage.setItem(VOICE_STORAGE_KEY, selectedVoice); } catch (e) { /* localStorage unavailable */ }
  }, [selectedVoice]);
  // ────────── Internal helpers ──────────

  /**
   * Build the dataChannel onmessage handler. Bound to current executor.
   * Returns the same handler shape used by both `connect()` and `renewSession()`.
   */
  const buildDataChannelHandler = useCallback((executor) => {
    return (ev) => {
      let msg;
      try {
        msg = JSON.parse(ev.data);
      } catch (_) {
        return;
      }
      // D4: any conversation-activity event resets the idle-disconnect timer.
      if (sessionLimitRef.current && IDLE_ACTIVITY_EVENT_TYPES.has(msg.type)) {
        sessionLimitRef.current.registerActivity();
      }
      // CRITICAL-1: AI output transcript only (Phase 2 scope).
      // P2 hotfix: canonical OpenAI Realtime API GA event is
      // `response.output_audio_transcript.done` (matches the delta event
      // namespace). Legacy `response.audio_transcript.done` retained for
      // backward compat.
      if (
        msg.type === 'response.output_audio_transcript.done'
        || msg.type === 'response.audio_transcript.done'
      ) {
        if (isMountedRef.current) setAiCaptionText(msg.transcript || '');
      } else if (msg.type === 'response.output_audio_transcript.delta') {
        if (isMountedRef.current) {
          setAiCaptionText((prev) => (prev || '') + (msg.delta || ''));
        }
      }
      // P1-1 Q4 太子判定: 0 legacy fallback. canonical GA event のみ受信.
      // typed schema `conversation_item_input_audio_transcription_completed_event.py`
      // で transcript: str + item_id: str 最上層 field 確認済 (openai-python).
      // P2 turn-safety (六次審査): per-`item_id` keying — multiple in-flight
      // utterances do not mix; out-of-order `.completed` does not clobber
      // prior items; `.delta` after `.completed` for same item_id is no-op
      // (protocol-violation defense). Input-type defense (sub-7 拡張):
      // missing item_id or non-string transcript/delta gracefully handled.
      else if (msg.type === 'conversation.item.input_audio_transcription.completed') {
        if (isMountedRef.current && typeof msg.item_id === 'string') {
          // C1 race fix v2 (post-I3 GREEN exposure): assign ref SYNCHRONOUSLY
          // BEFORE setState — React 18 batches setState and runs the updater
          // in commit phase AFTER current event handler. If a synchronous
          // subsequent event (e.g. function_call_arguments.done for
          // log_voice_turn in the same tick) reads the ref, the
          // setState-callback-inline pattern would still observe the stale
          // value. Ref is the synchronous source of truth; React state
          // mirrors it for re-render. Do not sync committed state back into
          // the ref: a concurrent stale commit could erase a newer delta.
          const next = {
            ...studentTranscriptsRef.current,
            [msg.item_id]: {
              transcript: typeof msg.transcript === 'string' ? msg.transcript : '',
              completed: true,
            },
          };
          studentTranscriptsRef.current = next;
          setStudentTranscripts(next);
        }
      } else if (msg.type === 'conversation.item.input_audio_transcription.delta') {
        // typed schema で delta: Optional[str] (Q1 既解、null 許容).
        if (isMountedRef.current && typeof msg.item_id === 'string') {
          const existing = studentTranscriptsRef.current[msg.item_id];
          // .delta after .completed = no-op (protocol-violation defense).
          // Ref already reflects the completed state; early-return preserves it.
          if (existing && existing.completed) {
            return;
          }
          // C1 race fix v2: same pattern as `.completed` above — ref FIRST,
          // setState SECOND. Reads from ref (synchronous source of truth)
          // to compute next state.
          const next = {
            ...studentTranscriptsRef.current,
            [msg.item_id]: {
              transcript: (existing?.transcript || '') + (
                typeof msg.delta === 'string' ? msg.delta : ''
              ),
              completed: false,
            },
          };
          studentTranscriptsRef.current = next;
          setStudentTranscripts(next);
        }
      }
      // tool_call dispatch (function_call_arguments.done is the canonical
      // Realtime API event for finalized tool calls).
      if (msg.type === 'response.function_call_arguments.done') {
        let parsedArgs;
        try {
          parsedArgs = typeof msg.arguments === 'string'
            ? JSON.parse(msg.arguments)
            : (msg.arguments || {});
        } catch (_) {
          parsedArgs = {};
        }

        // Phase 3 Option B (v2.14.4-p1-1-whisper-sidecar-p3-option-b):
        // augment log_voice_turn args with frontend per-item_id transcript
        // state read from `studentTranscriptsRef.current` (fresh via the
        // ref-FIRST-setState-SECOND sync in the .completed/.delta handlers
        // above — C1 race-fix v2 post-hotfix¹; the rejected v1 pattern
        // setState-callback-inline runs in commit phase AFTER current
        // event handler, too late for same-tick reads). AI side does not
        // populate `student_transcripts` (no awareness of frontend-only
        // state); the field IS NOT declared in the OpenAI Realtime tool
        // schema per 太子 C2 decision — backend dispatcher accepts it via
        // Dict[str, Any] router-layer propagation and applies idempotent
        // upsert by item_id (hotfix²). Set-after-spread order protects
        // against any AI-supplied override
        // (sub-7 spread-after-set anti-pattern guard, prior 4-layer defense
        // pattern recently re-affirmed by realtimeUiActionExecutor.js fix).
        if (msg.name === 'log_voice_turn') {
          const entries = studentTranscriptsRef.current || {};
          parsedArgs = {
            ...parsedArgs,
            student_transcripts: Object.entries(entries)
              .map(([item_id, e]) => ({
                item_id,
                transcript: typeof e?.transcript === 'string' ? e.transcript : '',
                completed: e?.completed === true,
              }))
              .slice(-32),  // maxItems cap mirror of backend
          };
        }

        executor({
          callId: msg.call_id,
          toolName: msg.name,
          args: parsedArgs,
        }).catch((err) => {
          logClientWarn('[useRealtimeVoice] executor error', err);
        });
      }
    };
  }, []);

  /**
   * Hook ontrack onto a PC — wires the remote audio stream to the
   * `remoteAudioRef` <audio> element.
   */
  const attachRemoteAudio = useCallback((pc) => {
    pc.ontrack = (ev) => {
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = ev.streams[0];
        // Autoplay can be blocked by some browser policies — swallow errors.
        const playPromise = remoteAudioRef.current.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      }
    };
  }, []);

  /**
   * Map ephemeral_token + expires_at error response to JP UX message.
   * Falls back to generic session_unavailable.
   */
  const surfaceSessionError = useCallback((err) => {
    const code = err?.response?.data?.detail?.error_code;
    if (code && REALTIME_ERROR_CODE_MAP[code]) {
      setErrorCode(code);
      setErrorMessage(REALTIME_ERROR_CODE_MAP[code]);
    } else {
      setErrorCode('realtime_session_unavailable');
      setErrorMessage(REALTIME_ERROR_CODE_MAP.realtime_session_unavailable);
    }
    setConnectionState('error');
  }, []);

  // Forward declarations for cross-referenced callbacks.
  const disconnectRef = useRef(null);
  const connectRef = useRef(null);

  /**
   * cleanupCurrentAttempt() — P1 hotfix: release WebRTC + media resources
   * without touching React state. Used by both disconnect() (which then
   * resets state to idle) and by connect() SDP failure paths (which then
   * set error state). Idempotent + null-safe.
   *
   * Prevents zombie mic stream / PC / dc / renew timer / token refs /
   * window backdoor when connect() fails after streamRef + pcRef + dcRef
   * + ephemeralTokenRef + window backdoor are already populated.
   *
   * G-4 social contract (P1-1 + P2 caption text policy): DO NOT add
   * `setStudentTranscripts({})` OR `setAiCaptionText('')` here.
   * Caption text resets are routed through `disconnect()` only — so SDP
   * failure transitions RETAIN the last utterances for debug/inspection.
   * No automated test catches violations here directly — H3 covers the
   * related renewSession failure path (a sibling surface), not direct
   * additions to this function. The policy belongs at this site because
   * cleanupCurrentAttempt is the function that future refactors would
   * most plausibly add unnecessary state resets to; rely on code review
   * + this JSDoc as social contract.
   */
  const cleanupCurrentAttempt = useCallback(() => {
    // D4: stop the session-limit timers (idempotent; safe on partial attempts).
    if (sessionLimitRef.current) sessionLimitRef.current.stop();
    if (renewTimerRef.current) {
      clearTimeout(renewTimerRef.current);
      renewTimerRef.current = null;
    }
    if (dcRef.current) {
      try { dcRef.current.close(); } catch (_) { /* swallow */ }
      dcRef.current = null;
    }
    if (pcRef.current) {
      try { pcRef.current.close(); } catch (_) { /* swallow */ }
      pcRef.current = null;
    }
    // M1: defensive null check before stream cleanup.
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => {
        try { t.stop(); } catch (_) { /* swallow */ }
      });
      streamRef.current = null;
    }
    // C2: gated test backdoor — delete only when in non-production env.
    if (process.env.NODE_ENV !== 'production') {
      try { delete window.__realtimeDataChannel; } catch (_) { /* swallow */ }
    }
    ephemeralTokenRef.current = null;
    realtimeSessionIdRef.current = null;
  }, []);

  /**
   * disconnect() — release all WebRTC + media resources, reset state.
   * Idempotent. Safe to call from any state.
   * Delegates resource cleanup to cleanupCurrentAttempt().
   */
  const disconnect = useCallback(() => {
    cleanupCurrentAttempt();
    setShowRenewPrompt(false);
    // D4: clear the ending-soon warning; the post-end NOTICE is intentionally NOT
    // reset here (it must survive the disconnect() that onEnd triggers), only on a
    // fresh connect().
    setSessionEndingSoon(null);
    setConnectionState('idle');
    setAiCaptionText('');
    // P1-1 + P2 turn-safety: reset entire student transcripts map on disconnect.
    // G-4 policy: NOT reset inside cleanupCurrentAttempt() — resource cleanup
    // only there; React state resets routed through disconnect(). Retaining
    // the map across cleanupCurrentAttempt enables debug/inspection of all
    // in-flight + completed utterances immediately before an SDP failure
    // transition (per-item_id keying preserves whichever utterances were
    // active at failure time).
    setStudentTranscripts({});
    // Phase 3 Option B: also reset the ref to keep it atomic with state.
    // useEffect sync would catch this eventually but the ref is read by the
    // function_call_arguments.done handler — any in-flight log_voice_turn
    // tool call after disconnect must observe an empty map.
    studentTranscriptsRef.current = {};
    setErrorCode(null);
    setErrorMessage(null);
    setExpiresAt(null);
    setModelUsed(null);
  }, [cleanupCurrentAttempt]);

  // Keep disconnectRef in sync so connect()'s executor closure can reference it.
  useEffect(() => { disconnectRef.current = disconnect; }, [disconnect]);

  /**
   * connect() — establish session + WebRTC peer connection + data channel.
   * C9: race guard for concurrent / connected calls.
   * C8: getUserMedia with audio-only constraints, NotAllowedError → UX.
   * C7: token stored in useRef only.
   * C10: schedule renewal prompt 30s before expires_at.
   */
  const connect = useCallback(async () => {
    // P2-3 (post-e2e-completion): synchronous ref-based race guard. React
    // state updates are async, so 2 concurrent connect() calls in the same
    // render closure could BOTH observe connectionState='idle' before
    // either's setState flushed → both proceeded to allocate mic + PC +
    // ephemeral_token → second one wins refs, first one leaks. The ref
    // flips synchronously in the same task tick, so the second call's
    // read+set is guaranteed to see the first's set.
    if (connectInFlightRef.current) {
      return;
    }
    // State-based 2nd-layer guard. The ref above clears in this function's
    // outer `finally` (L444), but the PC's `onconnectionstatechange` fires
    // asynchronously AFTER finally clears — so between `finally` and the
    // first `connected` transition, there is a window where
    //   connectInFlightRef.current === false  AND  connectionState === 'connecting'
    // A second `connect()` invocation during that window MUST be blocked or
    // it will re-allocate mic + PC + ephemeral_token while the first PC is
    // still negotiating. Keeping BOTH 'connecting' and 'connected' covers:
    //   - 'connecting': the post-finally / pre-connected window described above
    //   - 'connected':  cross-tick "connect button still visible after connection"
    //                   case where UI hasn't re-rendered the disabled state yet
    if (connectionState === 'connecting' || connectionState === 'connected') {
      return;
    }
    connectInFlightRef.current = true;
    try {
      setConnectionState('connecting');
      setErrorCode(null);
      setErrorMessage(null);
      // D4: a fresh connect clears any prior auto-disconnect notice.
      setSessionLimitNotice(null);
      // D4: a USER-initiated connect starts a fresh wall-clock budget; an automatic
      // 401 auth-retry reconnect (authReconnectRef) preserves the existing deadline.
      const isAuthReconnect = authReconnectRef.current;
      authReconnectRef.current = false;
      if (!isAuthReconnect) {
        sessionStartedAtRef.current = null;
        sessionLimitEndedRef.current = false;
      }

      // C8: getUserMedia (audio only).
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS);
      } catch (err) {
        if (err && err.name === 'NotAllowedError') {
          setErrorCode('mic_denied');
          setErrorMessage('マイクの使用を許可してください');
          setConnectionState('error');
          return;
        }
        setErrorCode('mic_unavailable');
        setErrorMessage('マイクが利用できません。ブラウザの権限設定を確認してください。');
        setConnectionState('error');
        return;
      }

      // StrictMode unmount race — stop tracks if unmounted during await.
      if (!isMountedRef.current) {
        stream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
        return;
      }
      streamRef.current = stream;

      // C7: POST /api/realtime/session (axios attaches JWT via interceptor).
      // e2e-completion post-merge hotfix P1-1: pass `session_id` (DB Session
      // integer PK) so the backend can validate ownership and pin the trace
      // to the exact learning session this panel was opened for.
      let sessionData;
      try {
        const resp = await api.post('/api/realtime/session', {
          session_id: sessionIdRef.current,
          requested_voice: selectedVoiceRef.current,
          language: languageRef.current,
          expires_after_seconds: DEFAULT_EXPIRES_AFTER_SECONDS,
        });
        sessionData = resp.data;
      } catch (err) {
        surfaceSessionError(err);
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
          streamRef.current = null;
        }
        return;
      }

      const {
        ephemeral_token: ephemeralToken,
        expires_at: serverExpiresAt,
        realtime_session_id: rtSessionId,
        model_used: serverModelUsed,
      } = sessionData;

      // C7: useRef ONLY — token never reaches localStorage / sessionStorage.
      ephemeralTokenRef.current = ephemeralToken;
      realtimeSessionIdRef.current = rtSessionId;
      setExpiresAt(serverExpiresAt);
      setModelUsed(serverModelUsed);

      // C10: schedule renewal prompt 30s before expires_at.
      const leadMs = (serverExpiresAt * 1000) - Date.now() - TOKEN_RENEWAL_PROMPT_LEAD_MS;
      if (leadMs > 0) {
        renewTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) setShowRenewPrompt(true);
        }, leadMs);
      }

      // WebRTC setup.
      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      attachRemoteAudio(pc);

      // Data channel for events + tool_calls.
      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;

      // Executor closure — reads realtimeSessionIdRef.current at dispatch time.
      const executor = createUiActionExecutor({
        onApplyOverlay,
        onAuthRetry: () => {
          // 401 from /api/realtime/tool → token expired, disconnect + reconnect.
          // D4: mark this an automatic reconnect so the wall-clock deadline is preserved
          // (a token refresh must not grant a fresh full budget).
          authReconnectRef.current = true;
          if (disconnectRef.current) disconnectRef.current();
          if (connectRef.current) {
            // Defer reconnect to next tick to avoid sync state churn.
            setTimeout(() => {
              if (isMountedRef.current && connectRef.current) connectRef.current();
            }, 0);
          }
        },
        realtimeSessionIdRef,
      });
      dc.onmessage = buildDataChannelHandler(executor);

      // C2: test backdoor expose ONLY in non-production env.
      if (process.env.NODE_ENV !== 'production') {
        window.__realtimeDataChannel = dc;
      }

      // SDP offer/answer with OpenAI Realtime API.
      let sdpAnswer;
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const sdpResp = await fetch(buildOpenAIRealtimeSdpUrl(serverModelUsed), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${ephemeralToken}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp,
        });
        if (!sdpResp.ok) {
          // P1 hotfix: release mic stream + PC + dc + renew timer + refs before
          // surfacing error (prevents zombie resources on retry).
          cleanupCurrentAttempt();
          setErrorCode('realtime_session_unavailable');
          setErrorMessage(REALTIME_ERROR_CODE_MAP.realtime_session_unavailable);
          setConnectionState('error');
          return;
        }
        sdpAnswer = await sdpResp.text();
      } catch (err) {
        logClientWarn('[useRealtimeVoice] SDP exchange failed', err);
        // P1 hotfix: cleanup before error state.
        cleanupCurrentAttempt();
        setErrorCode('realtime_session_unavailable');
        setErrorMessage(REALTIME_ERROR_CODE_MAP.realtime_session_unavailable);
        setConnectionState('error');
        return;
      }

      try {
        await pc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });
      } catch (err) {
        logClientWarn('[useRealtimeVoice] setRemoteDescription failed', err);
        // P1 hotfix: cleanup before error state.
        cleanupCurrentAttempt();
        setErrorCode('realtime_session_unavailable');
        setErrorMessage(REALTIME_ERROR_CODE_MAP.realtime_session_unavailable);
        setConnectionState('error');
        return;
      }

      pc.onconnectionstatechange = () => {
        if (!isMountedRef.current) return;
        if (pc.connectionState === 'connected') {
          setConnectionState('connected');
          // D4: arm the caps with the REMAINING wall-clock budget. On a fresh session
          // sessionStartedAtRef is null → full duration; on an auth-retry reconnect it
          // is preserved → the reconnect resumes the original deadline. performance.now()
          // is monotonic — a system clock adjustment must not extend the hard cap.
          if (sessionStartedAtRef.current === null) sessionStartedAtRef.current = performance.now();
          if (sessionLimitRef.current) {
            sessionLimitRef.current.start(
              computeRemainingMax(sessionStartedAtRef.current, performance.now(), MAX_SESSION_DURATION_MS),
            );
          }
        } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
          // D4: terminal failure → stop the caps and clear any ending-soon warning so the
          // panel does not show a stale "session ending" note next to the error.
          if (sessionLimitRef.current) sessionLimitRef.current.stop();
          setSessionEndingSoon(null);
          setConnectionState('error');
          setErrorCode('realtime_session_unavailable');
          setErrorMessage(REALTIME_ERROR_CODE_MAP.realtime_session_unavailable);
        }
      };
    } finally {
      // P2-3: clear the in-flight flag on EVERY exit path (success, mic
      // denial, /session error, all 3 SDP failure cleanup paths, generic
      // catch). Without this, a failed connect leaves the flag set and
      // the next retry click silently no-ops.
      connectInFlightRef.current = false;
    }
  }, [
    connectionState,
    attachRemoteAudio,
    buildDataChannelHandler,
    onApplyOverlay,
    surfaceSessionError,
    cleanupCurrentAttempt,
  ]);

  // Keep connectRef in sync for use inside executor closure.
  useEffect(() => { connectRef.current = connect; }, [connect]);

  /**
   * renewSession() — C3 5-step graceful sequential renewal.
   *   Step 1: fetch new ephemeral_token from server.
   *   Step 2: build new RTCPeerConnection + new mic stream.
   *   Step 3: SDP offer/answer for new connection.
   *   Step 4: wait for new PC to reach `connected` (safety timeout).
   *   Step 5: only then close old PC / dc / stream (graceful swap).
   *
   * Rollback on any failure: clean up partial new resources, keep old.
   */
  const renewSession = useCallback(async () => {
    if (!isMountedRef.current) return;
    // P2-3: ref-based race guard for renewSession. Without this, a user
    // double-clicking the renewal prompt would trigger 2 concurrent
    // renews → 2 new mic streams + 2 new RTCPeerConnections; only one
    // wins refs at Step 5, the orphan leaks (mic indicator stays on
    // after disconnect, PC never closes).
    if (renewInFlightRef.current) return;
    renewInFlightRef.current = true;
    try {
      setShowRenewPrompt(false);

      const oldPc = pcRef.current;
      const oldDc = dcRef.current;
      const oldStream = streamRef.current;
      const oldRenewTimer = renewTimerRef.current;

      // Step 1: fetch new ephemeral_token.
      // P1-1: same session_id pinning as initial connect (the user is renewing
      // the SAME learning session token; backend re-validates ownership).
      let newSessionData;
      try {
        const resp = await api.post('/api/realtime/session', {
          session_id: sessionIdRef.current,
          requested_voice: selectedVoiceRef.current,
          language: languageRef.current,
          expires_after_seconds: DEFAULT_EXPIRES_AFTER_SECONDS,
        });
        newSessionData = resp.data;
      } catch (err) {
        logClientWarn('[useRealtimeVoice.renewSession] fetch failed — keeping old', err);
        // Step 5 rollback: keep old connection + restore renewal prompt for retry.
        if (isMountedRef.current) setShowRenewPrompt(true);
        return;
      }

      // Step 2: new mic stream + new RTCPeerConnection.
      let newStream;
      try {
        newStream = await navigator.mediaDevices.getUserMedia(AUDIO_CONSTRAINTS);
      } catch (err) {
        logClientWarn('[useRealtimeVoice.renewSession] getUserMedia failed — keeping old', err);
        if (isMountedRef.current) setShowRenewPrompt(true);
        return;
      }
      if (!isMountedRef.current) {
        newStream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
        return;
      }

      const newPc = new RTCPeerConnection();
      newStream.getTracks().forEach((t) => newPc.addTrack(t, newStream));
      attachRemoteAudio(newPc);

      const newDc = newPc.createDataChannel('oai-events');
      const executor = createUiActionExecutor({
        onApplyOverlay,
        onAuthRetry: () => {
          // D4: preserve the wall-clock deadline across the automatic reconnect.
          authReconnectRef.current = true;
          if (disconnectRef.current) disconnectRef.current();
          if (connectRef.current) {
            setTimeout(() => {
              if (isMountedRef.current && connectRef.current) connectRef.current();
            }, 0);
          }
        },
        realtimeSessionIdRef,
      });
      newDc.onmessage = buildDataChannelHandler(executor);

      // Step 3: SDP offer/answer for new connection.
      try {
        const offer = await newPc.createOffer();
        await newPc.setLocalDescription(offer);
        const sdpResp = await fetch(buildOpenAIRealtimeSdpUrl(newSessionData.model_used), {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${newSessionData.ephemeral_token}`,
            'Content-Type': 'application/sdp',
          },
          body: offer.sdp,
        });
        if (!sdpResp.ok) {
          // Step 5 rollback: clean up new resources.
          try { newDc.close(); } catch (_) {}
          try { newPc.close(); } catch (_) {}
          newStream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
          console.warn('[useRealtimeVoice.renewSession] SDP non-OK — keeping old');
          if (isMountedRef.current) setShowRenewPrompt(true);
          return;
        }
        const sdpAnswer = await sdpResp.text();
        await newPc.setRemoteDescription({ type: 'answer', sdp: sdpAnswer });
      } catch (err) {
        try { newDc.close(); } catch (_) {}
        try { newPc.close(); } catch (_) {}
        newStream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
        logClientWarn('[useRealtimeVoice.renewSession] SDP exchange failed — keeping old', err);
        if (isMountedRef.current) setShowRenewPrompt(true);
        return;
      }

      // Step 4: wait for new PC to reach `connected` (safety timeout).
      const waitConnected = new Promise((resolve) => {
        let settled = false;
        const safetyTimer = setTimeout(() => {
          if (settled) return;
          settled = true;
          resolve({ ok: false, reason: 'timeout' });
        }, RENEW_CONNECT_TIMEOUT_MS);
        newPc.onconnectionstatechange = () => {
          if (!isMountedRef.current) {
            if (settled) return;
            settled = true;
            clearTimeout(safetyTimer);
            resolve({ ok: false, reason: 'unmount' });
            return;
          }
          if (newPc.connectionState === 'connected') {
            if (settled) return;
            settled = true;
            clearTimeout(safetyTimer);
            resolve({ ok: true });
          } else if (newPc.connectionState === 'disconnected' || newPc.connectionState === 'failed') {
            if (settled) return;
            settled = true;
            clearTimeout(safetyTimer);
            resolve({ ok: false, reason: newPc.connectionState });
          }
        };
      });
      const waitResult = await waitConnected;
      // D4: also abort if the session-limit ended (or the hook unmounted) DURING the
      // renewal — otherwise the swap below would revive the call past the hard cap.
      if (!waitResult.ok || sessionLimitEndedRef.current || !isMountedRef.current) {
        try { newDc.close(); } catch (_) {}
        try { newPc.close(); } catch (_) {}
        newStream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
        console.warn('[useRealtimeVoice.renewSession] renewal aborted', waitResult.reason || 'session-ended/unmounted');
        // Only re-offer renewal on a genuine connect failure — not when the limit ended.
        if (!waitResult.ok && isMountedRef.current && !sessionLimitEndedRef.current) setShowRenewPrompt(true);
        return;
      }

      // Step 5: swap in new resources, THEN close old (graceful).
      pcRef.current = newPc;
      dcRef.current = newDc;
      streamRef.current = newStream;
      ephemeralTokenRef.current = newSessionData.ephemeral_token;
      realtimeSessionIdRef.current = newSessionData.realtime_session_id;
      setExpiresAt(newSessionData.expires_at);
      setModelUsed(newSessionData.model_used);

      // Wire post-swap connectionstatechange (replace the resolve-once handler).
      newPc.onconnectionstatechange = () => {
        if (!isMountedRef.current) return;
        if (newPc.connectionState === 'disconnected' || newPc.connectionState === 'failed') {
          // D4: terminal failure → stop the caps + clear the ending-soon warning (mirror of connect()).
          if (sessionLimitRef.current) sessionLimitRef.current.stop();
          setSessionEndingSoon(null);
          setConnectionState('error');
          setErrorCode('realtime_session_unavailable');
          setErrorMessage(REALTIME_ERROR_CODE_MAP.realtime_session_unavailable);
        }
      };
      setConnectionState('connected');

      // Close old resources AFTER swap completes (graceful sequential).
      if (oldRenewTimer) {
        clearTimeout(oldRenewTimer);
      }
      if (oldDc) {
        try { oldDc.close(); } catch (_) {}
      }
      if (oldPc) {
        try { oldPc.close(); } catch (_) {}
      }
      if (oldStream) {
        oldStream.getTracks().forEach((t) => { try { t.stop(); } catch (_) {} });
      }

      // Re-schedule renewal prompt for the new token.
      const leadMs = (newSessionData.expires_at * 1000) - Date.now() - TOKEN_RENEWAL_PROMPT_LEAD_MS;
      if (leadMs > 0) {
        renewTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) setShowRenewPrompt(true);
        }, leadMs);
      } else {
        renewTimerRef.current = null;
      }

      // C2: refresh test backdoor pointer to new dc.
      if (process.env.NODE_ENV !== 'production') {
        window.__realtimeDataChannel = newDc;
      }
    } finally {
      renewInFlightRef.current = false;
    }
  }, [
    attachRemoteAudio,
    buildDataChannelHandler,
    onApplyOverlay,
  ]);

  // ────────── Mount / unmount cleanup ──────────
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // D4: stop session-limit timers on unmount.
      if (sessionLimitRef.current) sessionLimitRef.current.stop();
      if (renewTimerRef.current) {
        clearTimeout(renewTimerRef.current);
        renewTimerRef.current = null;
      }
      if (dcRef.current) {
        try { dcRef.current.close(); } catch (_) {}
        dcRef.current = null;
      }
      if (pcRef.current) {
        try { pcRef.current.close(); } catch (_) {}
        pcRef.current = null;
      }
      // M1: defensive null check.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => {
          try { t.stop(); } catch (_) {}
        });
        streamRef.current = null;
      }
      // C2: gated delete on unmount.
      if (process.env.NODE_ENV !== 'production') {
        try { delete window.__realtimeDataChannel; } catch (_) {}
      }
      ephemeralTokenRef.current = null;
      realtimeSessionIdRef.current = null;
    };
  }, []);

  return {
    connectionState,
    errorCode,
    errorMessage,
    selectedVoice,
    setSelectedVoice,
    aiCaptionText,
    // P1-1 + P2 turn-safety: per-`item_id` map. Phase 3 consumer
    // ([RealtimeVoiceTutorPanel.js](../components/RealtimeVoiceTutorPanel.js))
    // iterates over entries via `Object.entries` (insertion order preserved
    // for object keys per ES2015+) — landed in Option A
    // (v2.14.4-p1-1-whisper-sidecar-p3-ui-binding). Option B (log_voice_turn
    // schema extension) shipped in v2.14.4-p1-1-whisper-sidecar-p3-option-b
    // — outbound augmentation reads `studentTranscriptsRef.current` (see
    // ref declaration around L139). Option C (RealtimeVoiceTrace dedicated
    // persistence — dual-column: legacy TEXT + NEW `transcripts_json` JSON
    // with idempotent upsert + bootstrap migrator) shipped in
    // v2.14.4-p1-1-whisper-sidecar-p3-option-c.
    studentTranscripts,
    modelUsed,
    expiresAt,
    showRenewPrompt,
    sessionEndingSoon,
    sessionLimitNotice,
    remoteAudioRef,
    connect,
    disconnect,
    renewSession,
  };
}
